import { TableRow, TableCell } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { KeyValue, TableHeader } from "../../interfaces";
import {
  primaryHeaderColor,
  textHeaderColor,
  secondaryHeaderColor,
  additionalHeaderColor,
} from "../../refdata/constants";
import MultiHeaderTable from "./MultiHeaderTable";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

const PREFIX = "MultiHeaderSummaryTable";

const classes = {
  loadingSpinner: `${PREFIX}-loadingSpinner`,
  loadingText: `${PREFIX}-loadingText`,
  firstCellDarken: `${PREFIX}-firstCellDarken`,
  firstCellDarkest: `${PREFIX}-firstCellDarkest`,
  tableCellDarken: `${PREFIX}-tableCellDarken`,
  lastCellDarken: `${PREFIX}-lastCellDarken`,
  groupHover: `${PREFIX}-groupHover`,
  group: `${PREFIX}-group`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.loadingSpinner}`]: {
    marginTop: 24,
    width: "100%",
    textAlign: "center",
  },

  [`& .${classes.loadingText}`]: {
    fontSize: 20,
    width: "100%",
    textAlign: "center",
  },

  [`& .${classes.firstCellDarken}`]: {
    minWidth: 120,
    fontWeight: "bold",
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    textAlign: "left",
  },

  [`& .${classes.firstCellDarkest}`]: {
    minWidth: 120,
    fontWeight: "bold",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    textAlign: "left",
  },

  [`& .${classes.tableCellDarken}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },

  [`& .${classes.lastCellDarken}`]: {
    textAlign: "right",
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },

  [`& .${classes.groupHover}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      textDecoration: "underline",
      cursor: "pointer",
    },
  },

  [`& .${classes.group}`]: {
    textDecoration: "underline",
  },
}));

interface Props {
  tableData: KeyValue;
  totalsData: KeyValue;
  firstHeader: TableHeader[];
  tableType: string;
  firstColumnData?: KeyValue;
  showAsterisks?: boolean;
  loading?: boolean;
  lastYear?: string;
}

export default function MultiHeaderSummaryTable({
  tableData,
  totalsData,
  firstHeader,
  firstColumnData,
  tableType,
  showAsterisks,
  loading,
  lastYear,
}: Props) {
  const navigate = useNavigate();

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: "",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
          textAlign: "left",
        },
        colSpan: 3,
      },
      {
        title: "All Institutions",
        styles: {
          backgroundColor: secondaryHeaderColor,
          color: textHeaderColor,
          borderBottom: "1px solid",
        },
        colSpan: 3,
      },
      {
        title: "Commercial Banks",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
          borderBottom: "1px solid",
        },
        colSpan: 3,
      },
      {
        title: `Savings Institutions${
          lastYear && lastYear === "1994" ? "*" : ""
        }`,
        styles: {
          backgroundColor: secondaryHeaderColor,
          color: textHeaderColor,
          borderBottom: "1px solid",
        },
        colSpan: 3,
      },
      {
        title: "U.S. Branches of Foreign Banks",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
          borderBottom: "1px solid",
        },
        colSpan: 3,
      },
    ],
    (() => {
      const headerValues = [
        "Institutions",
        "Offices",
        "Deposits (Millions USD)",
      ];
      const headerValuesAsterisk = [
        "Institutions**",
        "Offices**",
        "Deposits (Millions USD)",
      ];

      const allInstitutions: TableHeader[] = (
        showAsterisks ? headerValuesAsterisk : headerValues
      ).map((header: string) => {
        return {
          title: header,
          styles: {
            backgroundColor: secondaryHeaderColor,
            color: textHeaderColor,
            minWidth: header === "Deposits (Millions USD)" ? 105 : 65,
            textAlign: "center",
          },
          colSpan: 1,
          apiField: "",
        };
      });
      const commercialBanks: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: {
              backgroundColor: additionalHeaderColor,
              color: textHeaderColor,
              minWidth: header === "Deposits (Millions USD)" ? 105 : 65,
              textAlign: "center",
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );
      const savingsInstitutions: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: {
              backgroundColor: secondaryHeaderColor,
              color: textHeaderColor,
              minWidth: header === "Deposits (Millions USD)" ? 105 : 65,
              textAlign: "center",
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );
      const foreignBanks: TableHeader[] = (
        showAsterisks ? headerValuesAsterisk : headerValues
      ).map((header: string) => {
        return {
          title: header,
          styles: {
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
            minWidth: header === "Deposits (Millions USD)" ? 105 : 65,
            textAlign: "center",
          },
          colSpan: 1,
          apiField: "",
        };
      });

      return [
        ...firstHeader,
        ...allInstitutions,
        ...commercialBanks,
        ...savingsInstitutions,
        ...foreignBanks,
      ];
    })(),
  ];
  const [groupHover, setGroupHover] = React.useState("");

  const tableCellProps = (
    instType: string,
    key: string,
    int: number,
    value: number,
  ) => {
    let props: KeyValue = { className: int % 2 ? classes.tableCellDarken : "" };
    if (value > 0) {
      props = {
        onMouseEnter: () => setGroupHover(`${instType}_${key}`),
        onMouseLeave: () => setGroupHover(""),
        onFocus: () => setGroupHover(`${instType}_${key}`),
        onBlur: () => setGroupHover(""),
        onClick: () =>
          navigate({
            search: `?summaryTableLevelTwo=true&instType=${instType}&${tableType}=${key}`,
          }),
        onKeyDown: (e: any) => {
          if (e.key === "Enter") {
            navigate({
              search: `?summaryTableLevelTwo=true&instType=${instType}&${tableType}=${key}`,
            });
          }
        },
        tabIndex: 0,
        className: clsx(
          int % 2 ? classes.tableCellDarken : "",
          groupHover === `${instType}_${key}`
            ? classes.groupHover
            : classes.group,
        ),
      };
    }
    return props;
  };

  const dataOrder = ["all", "CB", "SA", "IB"];

  const tableDataKeys = Object.keys(tableData);
  const getCountStyles = (
    count: number,
    overrides: React.CSSProperties = {},
  ): React.CSSProperties => {
    const base: React.CSSProperties = {
      color: count === 0 ? "grey" : "blue",
      ...(count === 0 && { textDecoration: "none" }),
    };

    return { ...base, ...overrides };
  };

  const tableDataRows =
    !loading &&
    tableDataKeys.length &&
    Object.keys(tableData[tableDataKeys.slice(-1)[0]]).length ? (
      Object.keys(tableData).map((key: string, index: number) => {
        return (
          <React.Fragment key={`TABLE_${tableData[key]}_${index}`}>
            <TableRow id={`dataSelectionRow-${index}`}>
              <TableCell
                colSpan={3}
                className={
                  index % 2 !== 0
                    ? classes.firstCellDarken
                    : classes.firstCellDarkest
                }
                style={{ textAlign: "left" }}
              >
                {firstColumnData
                  ? firstColumnData[index]
                  : tableDataKeys[index]}
              </TableCell>
              {dataOrder.map((instType: string, int) => {
                const instCount =
                  tableData[key][instType]?.sum_BKMO ||
                  tableData[key][instType]?.unique_count ||
                  0;
                const branchCount = tableData[key][instType]?.count;
                const depCount = tableData[key][instType]?.sum_DEPSUMBR;
                return (
                  <React.Fragment key={`${instType}_${int}`}>
                    <TableCell
                      {...tableCellProps(instType, key, int, instCount)}
                      style={getCountStyles(instCount)}
                    >
                      {instCount.toLocaleString()}
                    </TableCell>
                    <TableCell
                      {...tableCellProps(instType, key, int, branchCount)}
                      style={getCountStyles(branchCount)}
                    >
                      {branchCount?.toLocaleString()}
                    </TableCell>
                    <TableCell
                      {...tableCellProps(instType, key, int, depCount)}
                      style={getCountStyles(depCount)}
                    >
                      {(depCount / 1000).toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}
                    </TableCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          </React.Fragment>
        );
      })
    ) : (
      <></>
    );

  const totalDataKeys = Object.keys(totalsData);

  const totalsRow =
    !loading && totalDataKeys.length ? (
      <>
        <TableRow>
          <TableCell
            colSpan={3}
            style={{
              backgroundColor: primaryHeaderColor,
              color: textHeaderColor,
              textAlign: "left",
              fontWeight: "bold",
            }}
          >
            {firstHeader[0].title === "State" ||
            firstHeader[0].title === "County"
              ? "Totals / Unique Institutions"
              : firstHeader[0].title === "MSA"
                ? "MSA Total*"
                : "National Totals"}
          </TableCell>

          {dataOrder.map((key: string, inst) => {
            const oddKeys = ["CB", "IB"];
            const secondaryStyle: React.CSSProperties = {
              backgroundColor: oddKeys.includes(key)
                ? additionalHeaderColor
                : secondaryHeaderColor,
              color: textHeaderColor,
              textAlign: "center",
              fontWeight: "bold",
            };
            const thirdStyle: React.CSSProperties = {
              backgroundColor: oddKeys.includes(key)
                ? additionalHeaderColor
                : secondaryHeaderColor,
              color: textHeaderColor,
              textAlign: "center",
              fontWeight: "bold",
            };
            return (
              <React.Fragment key={`${key}_${inst}`}>
                <TableCell style={secondaryStyle}>
                  {totalsData[key]?.hasOwnProperty("sum_BKMO")
                    ? totalsData[key]?.sum_BKMO.toLocaleString()
                    : totalsData[key]?.unique_count?.toLocaleString()}
                </TableCell>
                <TableCell style={secondaryStyle}>
                  {totalsData[key]?.count?.toLocaleString()}
                </TableCell>
                <TableCell style={thirdStyle}>
                  {(totalsData[key]?.sum_DEPSUMBR / 1000)?.toLocaleString(
                    undefined,
                    {
                      maximumFractionDigits: 0,
                    },
                  )}
                </TableCell>
              </React.Fragment>
            );
          })}
        </TableRow>
      </>
    ) : (
      <></>
    );

  const tableBody = (
    <>
      {tableDataRows}
      {totalsRow}
    </>
  );
  return (
    <Root>
      <MultiHeaderTable
        useStickyHeader
        tableBody={tableBody}
        tableHeaders={tableHeaders}
      />
    </Root>
  );
}
